import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
// Types
export interface FilaEspera {
  id: string;
  inscricao: {
    id: string;
    status?: string;
    categoriaTorneio?: {
      id: string;
      nome: string;
    };
  };
  posicao: number;
  dataCriacao: string;
  dataConclusao?: string | null;
}

export interface FilaEsperaConnection {
  edges: FilaEspera[];
  totalCount: number;
}

export interface FilaEsperaInput {
  inscricaoId: string;
  posicao?: number;
}

// Queries
const FILA_ESPERA = gql`
  query FilaEspera($id: ID!) {
    filaEspera(id: $id) {
      id
      posicao
      dataCriacao
      dataConclusao
      inscricao {
        id
        status
        categoriaTorneio {
          id
          nome
        }
      }
    }
  }
`;

const FILAS_ESPERA = gql`
  query FilasEspera($page: Int, $size: Int, $sortBy: String, $sortDirection: String) {
    filasEspera(page: $page, size: $size, sortBy: $sortBy, sortDirection: $sortDirection) {
      edges {
        id
        posicao
        dataCriacao
        dataConclusao
        inscricao {
          id
          status
          categoriaTorneio {
            id
            nome
          }
        }
      }
      totalCount
    }
  }
`;

const FILAS_ESPERA_POR_CATEGORIA = gql`
  query FilasEsperaPorCategoria($categoriaTorneioId: ID!) {
    filasEsperaPorCategoria(categoriaTorneioId: $categoriaTorneioId) {
      id
      posicao
      dataCriacao
      dataConclusao
      inscricao {
        id
        status
        categoriaTorneio {
          id
          nome
        }
      }
    }
  }
`;

const FILAS_ESPERA_POR_TORNEIO = gql`
  query FilasEsperaPorTorneio($torneioId: ID!) {
    filasEsperaPorTorneio(torneioId: $torneioId) {
      id
      posicao
      dataCriacao
      dataConclusao
      inscricao {
        id
        status
        categoriaTorneio {
          id
          nome
        }
      }
    }
  }
`;

// Mutations
const CRIAR_FILA_ESPERA = gql`
  mutation CriarFilaEspera($input: FilaEsperaInput!) {
    criarFilaEspera(input: $input) {
      id
      posicao
      dataCriacao
      dataConclusao
      inscricao {
        id
        status
        categoriaTorneio {
          id
          nome
        }
      }
    }
  }
`;

const ATUALIZAR_FILA_ESPERA = gql`
  mutation AtualizarFilaEspera($id: ID!, $input: FilaEsperaInput!) {
    atualizarFilaEspera(id: $id, input: $input) {
      id
      posicao
      dataCriacao
      dataConclusao
      inscricao {
        id
        status
        categoriaTorneio {
          id
          nome
        }
      }
    }
  }
`;

const CONCLUIR_FILA_ESPERA = gql`
  mutation ConcluirFilaEspera($id: ID!) {
    concluirFilaEspera(id: $id) {
      id
      posicao
      dataCriacao
      dataConclusao
      inscricao {
        id
        status
        categoriaTorneio {
          id
          nome
        }
      }
    }
  }
`;

const EXCLUIR_FILA_ESPERA = gql`
  mutation ExcluirFilaEspera($id: ID!) {
    excluirFilaEspera(id: $id)
  }
`;

const PROMOVER_FILA_ESPERA = gql`
  mutation PromoverFilaEspera($categoriaTorneioId: ID, $filaEsperaId: ID) {
    promoverFilaEspera(categoriaTorneioId: $categoriaTorneioId, filaEsperaId: $filaEsperaId) {
      id
      posicao
      dataCriacao
      dataConclusao
      inscricao {
        id
        status
        categoriaTorneio {
          id
          nome
        }
      }
    }
  }
`;

// Service Functions
export const buscarFilaEspera = async (id: string): Promise<FilaEspera> => {
  const { data } = await apolloClient.query<{ filaEspera: FilaEspera }>({
    query: FILA_ESPERA,
    variables: { id },
    fetchPolicy: 'network-only',
  });
  return data.filaEspera;
};

export const buscarFilasEspera = async (
  page = 0,
  size = 10,
  sortBy = 'posicao',
  sortDirection = 'ASC'
): Promise<FilaEsperaConnection> => {
  const { data } = await apolloClient.query<{ filasEspera: FilaEsperaConnection }>({
    query: FILAS_ESPERA,
    variables: { page, size, sortBy, sortDirection },
    fetchPolicy: 'network-only',
  });
  return data.filasEspera;
};

export const buscarFilasEsperaPorCategoria = async (categoriaTorneioId: string): Promise<FilaEspera[]> => {
  const { data } = await apolloClient.query<{ filasEsperaPorCategoria: FilaEspera[] }>({
    query: FILAS_ESPERA_POR_CATEGORIA,
    variables: { categoriaTorneioId },
    fetchPolicy: 'network-only',
  });
  return data.filasEsperaPorCategoria;
};

export const buscarFilasEsperaPorTorneio = async (torneioId: string): Promise<FilaEspera[]> => {
  const { data } = await apolloClient.query<{ filasEsperaPorTorneio: FilaEspera[] }>({
    query: FILAS_ESPERA_POR_TORNEIO,
    variables: { torneioId },
    fetchPolicy: 'network-only',
  });
  return data.filasEsperaPorTorneio;
};

export const criarFilaEspera = async (input: FilaEsperaInput): Promise<FilaEspera> => {
  const { data } = await apolloClient.mutate<{ criarFilaEspera: FilaEspera }>({
    mutation: CRIAR_FILA_ESPERA,
    variables: { input },
  });
  if (!data?.criarFilaEspera) {
    throw new Error('Erro ao criar fila de espera');
  }
  return data.criarFilaEspera;
};

export const atualizarFilaEspera = async (id: string, input: FilaEsperaInput): Promise<FilaEspera> => {
  const { data } = await apolloClient.mutate<{ atualizarFilaEspera: FilaEspera }>({
    mutation: ATUALIZAR_FILA_ESPERA,
    variables: { id, input },
  });
  if (!data?.atualizarFilaEspera) {
    throw new Error('Erro ao atualizar fila de espera');
  }
  return data.atualizarFilaEspera;
};

export const concluirFilaEspera = async (id: string): Promise<FilaEspera> => {
  const { data } = await apolloClient.mutate<{ concluirFilaEspera: FilaEspera }>({
    mutation: CONCLUIR_FILA_ESPERA,
    variables: { id },
  });
  if (!data?.concluirFilaEspera) {
    throw new Error('Erro ao concluir fila de espera');
  }
  return data.concluirFilaEspera;
};

export const excluirFilaEspera = async (id: string): Promise<boolean> => {
  const { data } = await apolloClient.mutate<{ excluirFilaEspera: boolean }>({
    mutation: EXCLUIR_FILA_ESPERA,
    variables: { id },
  });
  return data?.excluirFilaEspera ?? false;
};

/**
 * Promove uma inscrição da fila de espera para Pendente.
 * - Se receber categoriaTorneioId: promove o próximo da fila de espera da categoria (ordem por posição).
 * - Se receber filaEsperaId: promove diretamente o registro especificado, independente da ordem da fila.
 * Pelo menos um dos campos deve ser informado.
 */
export const promoverFilaEspera = async (params: { categoriaTorneioId?: string; filaEsperaId?: string }): Promise<FilaEspera> => {
  const { data } = await apolloClient.mutate<{ promoverFilaEspera: FilaEspera }>({
    mutation: PROMOVER_FILA_ESPERA,
    variables: params,
  });
  if (!data?.promoverFilaEspera) {
    throw new Error('Erro ao promover inscrição da fila de espera');
  }
  return data.promoverFilaEspera;
};
